import { useCallback } from "react";
import { Formik, FormikProps } from "formik";
import { Row, Col } from "antd";
import { Form, InputNumber, SubmitButton } from "formik-antd";
import { useTranslation } from "react-i18next";
import { Setting as SettingType } from "/app/src/models";
import { settingService } from "/app/src/services";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { buildParams } from "/app/src/helpers";
import { handlePromiseError } from "/app/src/helpers/api";
/**
 * Component to display the form for updating API call log max count
 */
export default function ApiCallLogMaxCount() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  //useQuery for getting the API call log max count
  const { data: apiCallLogMaxCount } = useQuery({
    queryKey: ["apiCallLogMaxCount"],
    queryFn: () => {
      return settingService
        .getAll(buildParams({ name: "apiCallLogMaxCount" }))
        .then((response) => {
          return response.settings[0];
        });
    },
    initialData: { value: undefined },
  });
  //useMutation for updating the API call log max count
  const { mutateAsync: updateMaxCount } = useMutation({
    mutationFn: (updateMaxCount: SettingType) => {
      return settingService
        .updateSingle(updateMaxCount.id, { value: updateMaxCount.value })
        .then(handlePromiseError);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["apiCallLogMaxCount"], data.setting);
    },
  });
  //useMutation for creating the API call log max count
  const { mutateAsync: createMaxCount } = useMutation({
    mutationFn: (createMaxCount: SettingType) => {
      return settingService
        .createSingle({
          value: createMaxCount.value,
          name: "apiCallLogMaxCount",
          number: 112,
        })
        .then(handlePromiseError);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["apiCallLogMaxCount"], data.setting);
    },
  });
  //onSubmit handler for creating/updating the API call log max count
  const onSubmitMaxCount = useCallback(
    async (
      {
        value,
      }: {
        value: number;
      },
      {
        setFieldError,
      }: { setFieldError: (field: string, value: string) => void },
    ): Promise<void> => {
      const valueStr = String(value);
      if (apiCallLogMaxCount?.id) {
        await updateMaxCount({
          id: apiCallLogMaxCount.id,
          value: valueStr,
        }).catch((error) => {
          return setFieldError("value", error.errors[0]);
        });
      } else {
        await createMaxCount({ value: valueStr }).catch((error) => {
          return setFieldError("value", error.errors[0]);
        });
      }
    },
    [createMaxCount, updateMaxCount, apiCallLogMaxCount?.id],
  );
  /**
   * Update max count form
   */
  const updateMaxCountForm: (
    props: FormikProps<{ value: number }>,
  ) => JSX.Element = useCallback(
    ({ dirty }) => (
      <Form layout="vertical">
        <Row justify="start" gutter={16}>
          <Col span={8}>
            <h3>{`${t("translation:api_call_log_max_count")}`}</h3>
            <p>{t("translation:api_call_log_max_count_description")}</p>
          </Col>
          <Col span={13}>
            <Form.Item
              className="no-label" // skipcq: JS-0394
              name="value"
            >
              <InputNumber
                name="value"
                size="large"
                min={1}
                max={1000000}
                step={1000}
              />
            </Form.Item>
          </Col>
          <Col span={3}>
            <SubmitButton
              className="no-label" // skipcq: JS-0394
              type="primary"
              size="large"
              block
              disabled={!dirty}
            >
              {t("translation:save")}
            </SubmitButton>
          </Col>
        </Row>
      </Form>
    ),
    [t],
  );
  return (
    <Formik
      component={updateMaxCountForm}
      enableReinitialize
      initialValues={{
        value: apiCallLogMaxCount?.value
          ? parseInt(apiCallLogMaxCount.value, 10)
          : 1000000,
      }}
      onSubmit={onSubmitMaxCount}
    />
  );
}
